import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { noop, times, floor } from 'lodash';
import cx from 'classnames';

import questionTypes from 'sharedParts/config/questionTypes';
import personalLabelMap, { personalLabelProptypes } from 'sharedParts/config/personalLabelMap';
import { highlightColorsList } from 'sharedParts/config/blockConfig/questionScale';
import segmentLabelMap from 'sharedParts/config/segmentLabelMap';
import { isShapeSmile } from 'sharedParts/utils/validateScaleShapeType';
import StylingToCSSLabelText from 'styling/hocs/StylingToCSS/StylingToCSSLabelText';

import { getMirroredValue } from '../../../../utils/getMirroredValue';
import propTypeBlockStyling from '../../../../domains/styling/config/editorBlock/PropTypes/blockStyling';
import styles from './RatingScale.scss';
import RatingScaleItem from '../RatingScaleItem';

const RatingScale = (props) => {
  const {
    value = null,
    readOnly = false,
    isEditorMode = false,
    iconList,
    max,
    min = 0,
    className,
    containerClassName,
    indexClassName,
    segmentClassName,
    ratingItemClassName,
    block: { shape, itemList, segment, type, enableFillColor = false },
    labelType,
    fields,
    onChange = noop,
    count,
    shouldRenderIndex,
    startFromZero = false,
    styling,
    styleLabel,
    labelList,
  } = props;

  const INITIAL_INDEX = 0;
  const LAST_INDEX = itemList.length - 1;
  let labelIndex = -1;

  const getMidPointsIndexes = (arrayLength) => {
    const midPointsIndexes = [];
    midPointsIndexes.push(floor(arrayLength / 2));
    return midPointsIndexes;
  };

  const MID_POINTS_INDEX = getMidPointsIndexes(itemList.length);

  const normalizeMin = (min) => (min === -1 ? 0 : 1);

  const normalizeValue = (value) => {
    return labelType === personalLabelMap.numberDesc
      ? getMirroredValue({
          value,
          min,
          max,
          normalizeFn: normalizeMin,
        })
      : value;
  };

  const getGapSize = (itemListCount) => {
    const INITIAL_GAP_SIZE = 10;
    if (itemListCount <= 5) return `${INITIAL_GAP_SIZE}px`;
    if (itemListCount > 5) return `${INITIAL_GAP_SIZE + 5 - itemListCount}px`;
  };

  const [selectedValue, setSelectedValue] = useState(normalizeValue(value));
  const [selectedIndex, setSelectedIndex] = useState(value ? selectedValue - 1 : -1);

  const indexesList = labelType === personalLabelMap.numberDesc ? times(count).reverse() : times(count);

  const isFirstIndex = (index) => index === INITIAL_INDEX;

  const isLastIndex = (index) => index === LAST_INDEX;

  const isMidpointIndex = (index) => MID_POINTS_INDEX.includes(index);

  const shouldRenderLabel = (index) => {
    let shouldRender = false;
    if (segment === segmentLabelMap.itemList) {
      shouldRender = true;
    }
    if (segment === segmentLabelMap.labelListEnd && (isFirstIndex(index) || isLastIndex(index))) {
      shouldRender = true;
    }
    if (
      segment === segmentLabelMap.labelListEndMidpoint &&
      (isFirstIndex(index) || isMidpointIndex(index) || isLastIndex(index))
    ) {
      shouldRender = true;
    }
    return shouldRender;
  };

  const handleChange = ({ index, value }) => {
    if (readOnly) return;
    setSelectedIndex(index);
    const normalizedValue = normalizeValue(value);
    setSelectedValue(normalizedValue);
    onChange(normalizedValue);
  };

  const isItemSelected = (itemIndex) => itemIndex === selectedIndex;

  const gapSize = useMemo(() => getGapSize(count), [count]);

  return (
    <div
      className={cx(className, containerClassName, styles['rating-scale-container'])}
      style={{
        gap: gapSize,
        gridTemplateColumns: `repeat(5, 1fr)`,
        gridTemplateRows: `repeat(${count <= 5 ? 1 : 2}, 1fr)`,
      }}
    >
      {itemList.map((element, index) => {
        const icon = {
          empty: isShapeSmile(shape) ? iconList['empty'][index] : iconList['empty'],
          full: isShapeSmile(shape) ? iconList['full'][index] : iconList['full'],
        };
        const renderLabel = shouldRenderLabel(index);
        if (renderLabel) labelIndex++;
        const centerIconVertically =
          type === questionTypes.questionScale &&
          segment === segmentLabelMap.noSegment &&
          !shouldRenderIndex &&
          !renderLabel;
        return (
          <RatingScaleItem
            value={index + 1}
            key={index}
            isEditorMode={isEditorMode}
            index={index}
            indexLabel={indexesList[index]}
            labelName={`${segment}[${labelIndex}].value`}
            labelKey={fields && renderLabel ? fields.get(labelIndex).id : undefined}
            labelItem={labelList ? labelList[labelIndex] : undefined}
            ratingItemClassName={ratingItemClassName}
            indexClassName={indexClassName}
            segmentClassName={segmentClassName}
            labelType={labelType}
            enableFillColor={enableFillColor}
            startFromZero={startFromZero}
            icon={icon}
            handleChange={handleChange}
            isItemSelected={isItemSelected(index)}
            highlightColorsList={highlightColorsList[count]}
            styleLabel={styleLabel}
            questionType={type}
            shouldRenderIndex={shouldRenderIndex}
            shouldRenderLabel={renderLabel}
            centerIconVertically={centerIconVertically}
          />
        );
      })}
    </div>
  );
};

RatingScale.propTypes = {
  value: PropTypes.number,
  readOnly: PropTypes.bool,
  isEditorMode: PropTypes.bool,
  iconList: PropTypes.object.isRequired,
  max: PropTypes.number,
  min: PropTypes.number,
  className: PropTypes.string,
  containerClassName: PropTypes.string,
  indexClassName: PropTypes.string,
  segmentClassName: PropTypes.string,
  ratingItemClassName: PropTypes.string,
  block: PropTypes.shape({
    required: PropTypes.bool,
    shape: PropTypes.string.isRequired,
    color: PropTypes.string,
    personalLabelType: PropTypes.string,
    type: PropTypes.string.isRequired,
    enableFillColor: PropTypes.bool,
  }).isRequired,
  labelType: personalLabelProptypes,
  count: PropTypes.number.isRequired,
  fields: PropTypes.object,
  onChange: PropTypes.func,
  startFromZero: PropTypes.bool,
  shouldRenderIndex: PropTypes.bool,
  styling: propTypeBlockStyling,
  styleLabel: PropTypes.object,
  labelList: PropTypes.array,
};

export default StylingToCSSLabelText(RatingScale);
